<div class="page-header">
  <div class="page-title" data-clarity-unmask="true">Serwery multimediów</div>
  <div class="page-subtitle">
    satelickie kopie filmów i zdjęć odciążające api.testy-na-prawko.net
  </div>
</div>

<div class="media-servers">
  <div class="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
    <div class="flex align-items-center gap-2">
      <h4 class="m-0">Stan serwerów</h4>
      @if (loading) {
        <span class="refresh-hint">odświeżanie…</span>
      }
    </div>
    <p-button
      label="Odśwież"
      icon="pi pi-refresh"
      size="small"
      [outlined]="true"
      (onClick)="refresh()"
      data-clarity-unmask="true"
    />
  </div>

  @if (errorMessage) {
    <div class="error-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ errorMessage }}</span>
    </div>
  }

  @if (cards.length) {
    <div class="summary-grid">
      @for (card of cards; track card.label) {
        <div class="summary-card" [class.highlight]="card.highlight">
          <div class="summary-icon"><i [class]="card.icon"></i></div>
          <div class="summary-body">
            <div class="summary-label">{{ card.label }}</div>
            <div class="summary-value">{{ card.value }}</div>
            @if (card.detail) {
              <div class="summary-detail">{{ card.detail }}</div>
            }
          </div>
        </div>
      }
    </div>
  }

  @if (settings) {
    <div class="section-title">
      <i class="pi pi-cog"></i>
      <span>Ustawienia serwerów multimediów</span>
    </div>

    <div class="settings-group">
      <div class="setting-row">
        <p-checkbox
          inputId="originServesInRotation"
          [binary]="true"
          [ngModel]="settings.originServesInRotation"
          (ngModelChange)="onOriginRotationChange($event)"
          [disabled]="savingSettings"
        />
        <label for="originServesInRotation" class="setting-label">
          Serwer główny dostarcza media naprzemian z satelickimi
        </label>
        @if (savingSettings) {
          <span class="setting-saving">zapisywanie…</span>
        }
      </div>
      <div class="setting-hint">{{ rotationHint }}</div>
    </div>
  }

  @if (renditions) {
    <div class="section-title">
      <i class="pi pi-video"></i>
      <span>Wersje wideo dopasowane do ekranu</span>
    </div>

    <div class="settings-group renditions">
      <div class="setting-row">
        <p-tag [value]="renditions.generateEnabled ? 'Kodowanie włączone' : 'Kodowanie wyłączone'"
          [severity]="renditions.generateEnabled && renditions.ffmpegAvailable ? 'success' : 'secondary'" />
        <p-tag [value]="renditions.serveEnabled ? 'Wybór wersji włączony' : 'Wybór wersji wyłączony'"
          [severity]="renditions.serveEnabled ? 'success' : 'secondary'" />
        @if (!renditions.ffmpegAvailable && renditions.lastScanStartedUtc) {
          <p-tag value="Brak ffmpeg" severity="danger" />
        }
        <span class="setting-saving">wysokości: {{ renditions.heights.join(' / ') }} px</span>
      </div>

      <div class="renditions-stats">
        <div>
          <div class="summary-label">Gotowe wersje</div>
          <div class="summary-value">{{ renditionsProgress }}</div>
        </div>
        <div>
          <div class="summary-label">Filmy</div>
          <div class="summary-value">{{ formatNumber(renditions.videosTotal) }}</div>
          @if (renditions.videosUnreadable) {
            <div class="server-meta">{{ formatNumber(renditions.videosUnreadable) }} nieczytelnych</div>
          }
        </div>
        <div>
          <div class="summary-label">Zajętość</div>
          <div class="summary-value">{{ formatMb(renditions.renditionsSizeMb) }}</div>
        </div>
        <div>
          <div class="summary-label">Błędy kodowania</div>
          <div class="summary-value">{{ formatNumber(renditions.renditionsFailed) }}</div>
        </div>
      </div>

      @if (renditions.currentFile) {
        <div class="setting-hint">Kodowanie w toku: <span class="mono">{{ renditions.currentFile }}</span></div>
      } @else if (renditions.lastScanFinishedUtc) {
        <div class="setting-hint">Ostatnie sprawdzenie: {{ renditions.lastScanFinishedUtc | date: 'yyyy-MM-dd HH:mm' }}</div>
      }

      @if (renditions.sourceHeights.length) {
        <div class="source-heights">
          <span class="summary-label">Oryginały według wysokości:</span>
          @for (source of renditions.sourceHeights; track source.height) {
            <span class="source-height">{{ source.height }}p × {{ formatNumber(source.count) }}</span>
          }
        </div>
      }

      <div class="setting-hint">{{ renditionsHint }}</div>
    </div>
  }

  @if (loading && !summary) {
    <div class="flex justify-content-center py-5">
      <p-progressSpinner styleClass="w-3rem h-3rem" ariaLabel="loading" />
    </div>
  }

  @if (awaitingServers.length) {
    <div class="section-title awaiting">
      <i class="pi pi-clock"></i>
      <span>Zgłoszenia oczekujące na akceptację</span>
    </div>

    <div class="awaiting-grid">
      @for (server of awaitingServers; track server.guid) {
        <div class="awaiting-card">
          <div class="awaiting-head">
            <span class="awaiting-name">{{ server.serverName }}</span>
            <p-tag
              [value]="server.online ? 'połączony' : 'offline'"
              [severity]="server.online ? 'success' : 'danger'"
            />
          </div>
          <div class="awaiting-row">
            <span class="key">Adres</span>
            <span class="value mono">{{ server.publicUrl }}</span>
          </div>
          <div class="awaiting-row">
            <span class="key">Identyfikator</span>
            <span class="value mono">{{ server.machineId }}</span>
          </div>
          <div class="awaiting-row">
            <span class="key">System</span>
            <span class="value">{{ server.systemInformation || '—' }}</span>
          </div>
          <div class="awaiting-row">
            <span class="key">Wersja</span>
            <span class="value">{{ server.softwareVersion || '—' }}</span>
          </div>
          <div class="awaiting-row">
            <span class="key">Zgłoszony</span>
            <span class="value">
              {{ server.firstSeenDate | date: 'yyyy-MM-dd HH:mm' }}
            </span>
          </div>
          <div class="awaiting-actions">
            <p-button
              label="Akceptuj"
              icon="pi pi-check"
              size="small"
              [disabled]="isBusy(server)"
              (onClick)="accept(server)"
              data-clarity-unmask="true"
            />
            <p-button
              label="Usuń"
              icon="pi pi-trash"
              size="small"
              severity="danger"
              [outlined]="true"
              [disabled]="isBusy(server)"
              (onClick)="remove(server)"
              data-clarity-unmask="true"
            />
          </div>
        </div>
      }
    </div>
  }

  @if (summary) {
    <div class="section-title">
      <i class="pi pi-server"></i>
      <span>Zaakceptowane serwery</span>
    </div>

    @if (acceptedServers.length) {
      <div class="table-wrapper">
        <p-table [value]="acceptedServers" styleClass="p-datatable-sm" [scrollable]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>Nazwa</th>
              <th>Adres</th>
              <th>Status</th>
              <th class="num">Pliki</th>
              <th class="num">Zajętość</th>
              <th class="num">Obsłużone</th>
              <th class="num">Przekierowane</th>
              <th>Ostatni sygnał</th>
              <th>Akcje</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-server>
            <tr>
              <td>
                <div class="server-name">{{ server.serverName }}</div>
                <div class="server-meta mono">{{ server.machineId }}</div>
              </td>
              <td class="mono" data-label="Adres">{{ server.publicUrl }}</td>
              <td data-label="Status">
                <p-tag
                  [value]="statusLabel(server)"
                  [severity]="statusSeverity(server)"
                />
                @if (server.syncInProgress) {
                  <div class="server-meta">synchronizacja w toku…</div>
                }
              </td>
              <td class="num" data-label="Pliki">
                <span
                  [pTooltip]="'zmirrorowane / w katalogu źródłowym'"
                  tooltipPosition="top"
                >
                  {{ mirrorProgress(server) }}
                </span>
                @if (server.missingRenditionsCount) {
                  <div class="server-meta">
                    +{{ formatNumber(server.missingRenditionsCount) }} wersji wideo do pobrania
                  </div>
                }
              </td>
              <td class="num" data-label="Zajętość">{{ formatMb(server.storageUsedMb) }}</td>
              <td class="num" data-label="Obsłużone">
                <div>{{ formatNumber(server.servedRequests) }}</div>
                <div class="server-meta">{{ formatBytes(server.servedBytes) }}</div>
              </td>
              <td class="num" data-label="Przekierowane">{{ formatNumber(server.redirectedRequests) }}</td>
              <td data-label="Ostatni sygnał">
                {{
                  server.lastHeartbeatDate
                    ? (server.lastHeartbeatDate | date: 'yyyy-MM-dd HH:mm:ss')
                    : '—'
                }}
              </td>
              <td>
                <div class="row-actions">
                  <!-- Tooltip sits on the span: a disabled button swallows the mouse events pTooltip needs. -->
                  <span
                    [pTooltip]="enableBlockedReason(server)"
                    tooltipPosition="top"
                  >
                    <p-button
                      [label]="server.enabled ? 'Wyłącz' : 'Włącz'"
                      [icon]="server.enabled ? 'pi pi-pause' : 'pi pi-play'"
                      size="small"
                      [outlined]="true"
                      [disabled]="isBusy(server) || enableBlocked(server)"
                      (onClick)="toggleEnabled(server)"
                      data-clarity-unmask="true"
                    />
                  </span>
                  <p-button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    [text]="true"
                    pTooltip="Usuń serwer"
                    tooltipPosition="top"
                    [disabled]="isBusy(server)"
                    (onClick)="remove(server)"
                    data-clarity-unmask="true"
                  />
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    } @else {
      <div class="empty-state">
        Brak zaakceptowanych serwerów multimediów. Ruch do plików obsługuje
        wyłącznie serwer główny.
      </div>
    }
  }

  @if (summary && !summary.totalCount && !loading) {
    <div class="empty-state">
      Żaden serwer satelicki jeszcze się nie zgłosił. Uruchom aplikację
      PrawkoMedia ze wskazaniem na ten serwer — pojawi się tutaj do akceptacji.
    </div>
  }
</div>
